import { PART_TIME_TYPE, PART_TIME_CALCS, PART_TIME_CALC, calcByKey } from '../utils/partTime';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Grid, Button, Avatar, Chip, Stack, IconButton, Tooltip,
    TextField, MenuItem, Dialog, DialogContent, DialogActions, Snackbar, Alert,
    InputAdornment, Skeleton, CircularProgress,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ContactEmergencyRoundedIcon from '@mui/icons-material/ContactEmergencyRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import FolderSharedRoundedIcon from '@mui/icons-material/FolderSharedRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useOrgOptions from '../hooks/useOrgOptions';
import { selectUserTypeId, USER_TYPE } from '../redux/slices/authSlice';
import http, { apiErrorMessage } from '../Api/http';
import { GetEmployeeById, UpdateEmployee, SetEmployeeLoginPassword, InactivateEmployee } from '../Api/Api';
import { SettlementDialog } from './ResignationsPage';
import { toApiDate, toInputDate, fmtApiDate, numOrNull, txt } from '../utils/apiFields';
import { joinName, inr, companyExperience } from '../utils/format';
import EmployeeDocuments from '../components/EmployeeDocuments';
import EmployeeAttendanceSummary from '../components/EmployeeAttendanceSummary';

const PRIMARY = '#7C5CFC';
const PRIMARY_LIGHT = '#F1EEFE';
const PRIMARY_BORDER = '#C9BEFB';
const card = { bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', boxShadow: '0 1px 3px rgba(16,24,40,0.06)' };
const tonalBtn = { bgcolor: PRIMARY_LIGHT, color: PRIMARY, border: `1px solid ${PRIMARY_BORDER}`, fontWeight: 700, borderRadius: '7px', boxShadow: 'none', textTransform: 'none', '&:hover': { bgcolor: '#E7DFFC' } };
const solidBtn = { bgcolor: PRIMARY, color: '#fff', fontWeight: 700, borderRadius: '7px', boxShadow: `0 2px 6px ${PRIMARY}40`, textTransform: 'none', '&:hover': { bgcolor: '#6246E0' }, '&.Mui-disabled': { bgcolor: PRIMARY, color: '#fff', opacity: 0.65, boxShadow: 'none' } };
const ghostBtn = { color: '#475569', bgcolor: '#fff', border: '1px solid #E5E7EB', fontWeight: 700, borderRadius: '7px', textTransform: 'none', '&:hover': { bgcolor: '#F8FAFC' } };
const dangerBtn = { bgcolor: '#fff', color: '#E11D48', border: '1px solid #FBCFE8', fontWeight: 700, borderRadius: '7px', boxShadow: 'none', textTransform: 'none', '&:hover': { bgcolor: '#FEF2F2', borderColor: '#F9A8D4' } };
const REMOVE_PHRASE = 'confirmed';
const PASSWORD_MIN = 8;
const field = { '& .MuiOutlinedInput-root': { borderRadius: '7px', fontSize: 14, bgcolor: '#F8FAFC', '& fieldset': { borderColor: '#E5E7EB' }, '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: 1.5 } } };

export const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say'];
export const MARITAL = ['Single', 'Married', 'Divorced', 'Widowed'];
export const BLOOD = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];
const EMP_TYPES = ['Permanent', 'Probation', 'Contract', 'Internship', 'Part-Time'];

const todayInput = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const initials = (s = '') => s.split(' ').filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

const SECTIONS = [
    {
        id: 'personal', title: 'Personal Information', icon: PersonRoundedIcon, fields: [
            { k: 'firstName', label: 'First Name' },
            { k: 'lastName', label: 'Last Name' },
            { k: 'gender', label: 'Gender', type: 'select', options: GENDERS },
            { k: 'dateOfBirth', label: 'Date of Birth', type: 'date' },
            { k: 'maritalStatus', label: 'Marital Status', type: 'select', options: MARITAL },
            { k: 'bloodGroup', label: 'Blood Group', type: 'select', options: BLOOD },
            { k: 'nationality', label: 'Nationality' },
            { k: 'personalEmail', label: 'Personal Email' },
            { k: 'personalMobile', label: 'Personal Mobile' },
            { k: 'alternateMobile', label: 'Alternate Mobile' },
        ],
    },
    {
        id: 'employment', title: 'Employment Information', icon: WorkRoundedIcon, fields: [
            { k: 'companyEmployeeId', label: 'Company Employee ID' },
            { k: 'dateOfJoining', label: 'Date of Joining', type: 'date' },
            { k: 'employmentType', label: 'Employment Type', type: 'select', options: EMP_TYPES },
            { k: 'department', label: 'Department', type: 'select', options: [] },
            { k: 'designation', label: 'Designation', type: 'select', options: [] },
            { k: 'probationPeriodMonths', label: 'Probation Period (months)', num: true },
            { k: 'confirmationDate', label: 'Confirmation Date', type: 'date' },
        ],
    },
    {
        id: 'identity', title: 'Government & Identity', icon: BadgeRoundedIcon, fields: [
            { k: 'aadhaarNumber', label: 'Aadhaar Number' }, { k: 'panNumber', label: 'PAN Number' },
            { k: 'passportNumber', label: 'Passport Number' }, { k: 'drivingLicense', label: 'Driving License' },
            { k: 'voterId', label: 'Voter ID' }, { k: 'uanNumber', label: 'UAN Number' },
            { k: 'esiNumber', label: 'ESI Number' }, { k: 'pfNumber', label: 'PF Number' },
        ],
    },
    {
        id: 'emergency', title: 'Emergency Contact', icon: ContactEmergencyRoundedIcon, fields: [
            { k: 'emergencyContactName', label: 'Contact Name' }, { k: 'emergencyRelationship', label: 'Relationship' },
            { k: 'emergencyMobile', label: 'Mobile Number' }, { k: 'emergencyAlternateNumber', label: 'Alternate Number' },
            { k: 'emergencyAddress', label: 'Address' },
        ],
    },
    {
        id: 'bank', title: 'Bank Details', icon: AccountBalanceRoundedIcon, fields: [
            { k: 'accountHolderName', label: 'Account Holder Name' }, { k: 'bankName', label: 'Bank Name' },
            { k: 'accountNumber', label: 'Account Number' }, { k: 'ifsc', label: 'IFSC Code' },
            { k: 'branch', label: 'Branch' },
        ],
    },
];

// The page is nine full-width sections deep; the rail turns that scroll into a
// map you can jump around, and shows where you are while you read.
const PAGE_SECTIONS = [
    { id: 'credentials', label: 'Login Credentials', icon: LockRoundedIcon },
    { id: 'personal', label: 'Personal', icon: PersonRoundedIcon },
    { id: 'employment', label: 'Employment', icon: WorkRoundedIcon },
    { id: 'increments', label: 'Increments', icon: TrendingUpRoundedIcon },
    { id: 'contact', label: 'Contact & Address', icon: HomeRoundedIcon },
    { id: 'identity', label: 'Government & Identity', icon: BadgeRoundedIcon },
    { id: 'education', label: 'Education', icon: SchoolRoundedIcon },
    { id: 'previous', label: 'Previous Employment', icon: HistoryRoundedIcon },
    { id: 'emergency', label: 'Emergency Contact', icon: ContactEmergencyRoundedIcon },
    { id: 'bank', label: 'Bank Details', icon: AccountBalanceRoundedIcon },
    { id: 'documents', label: 'Documents', icon: FolderSharedRoundedIcon },
];

function SectionRail({ activeId, onJump, top }) {
    return (
        // The outer box is the grid item and stretches to the row's full height;
        // the sticky card lives inside it. Sticking the grid item itself does
        // nothing — it is exactly as tall as its content, so there is no travel.
        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
            <Box
                component="nav"
                sx={{
                    position: 'sticky', top,
                    ...card, borderRadius: '11px', p: 1.2,
                }}
            >
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#98A0AE', textTransform: 'uppercase', letterSpacing: 0.7, px: 1.2, pt: 0.6, pb: 1 }}>
                On this page
            </Typography>
            <Stack spacing={0.2}>
                {PAGE_SECTIONS.map((s) => {
                    const on = s.id === activeId;
                    return (
                        <Stack
                            key={s.id}
                            direction="row"
                            spacing={1.2}
                            onClick={() => onJump(s.id)}
                            sx={{
                                alignItems: 'center', px: 1.2, py: 1, borderRadius: '8px', cursor: 'pointer',
                                position: 'relative', userSelect: 'none',
                                bgcolor: on ? PRIMARY_LIGHT : 'transparent',
                                transition: 'background-color .15s, color .15s',
                                '&:hover': { bgcolor: on ? PRIMARY_LIGHT : '#F6F7FB' },
                                '&::before': on ? {
                                    content: '""', position: 'absolute', left: 0, top: 7, bottom: 7,
                                    width: 3, borderRadius: 3, bgcolor: PRIMARY,
                                } : {},
                            }}
                        >
                            <s.icon sx={{ fontSize: 17, color: on ? PRIMARY : '#98A0AE', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 13, fontWeight: on ? 800 : 600, color: on ? PRIMARY : '#475569' }} noWrap>
                                {s.label}
                            </Typography>
                        </Stack>
                    );
                })}
                </Stack>
            </Box>
        </Box>
    );
}

const ALL_FIELDS = SECTIONS.flatMap((s) => s.fields);

const EDITABLE = new Set([
    'firstName', 'lastName', 'gender', 'dateOfBirth', 'maritalStatus', 'bloodGroup',
    'nationality', 'personalEmail', 'personalMobile', 'alternateMobile',
    'companyEmployeeId', 'employmentType', 'dateOfJoining', 'department', 'designation',
    'probationPeriodMonths', 'confirmationDate',
    'aadhaarNumber', 'panNumber', 'passportNumber', 'drivingLicense',
    'voterId', 'uanNumber', 'esiNumber', 'pfNumber',
    'emergencyContactName', 'emergencyRelationship', 'emergencyMobile',
    'emergencyAlternateNumber', 'emergencyAddress',
    'accountHolderName', 'bankName', 'accountNumber', 'ifsc', 'branch',
]);

const EDITABLE_FIELDS = ALL_FIELDS.filter((f) => EDITABLE.has(f.k));

const MAX_EDU = 5;
const MAX_PREV = 10;
const EDU_QUALIFICATIONS = ['10th', '12th', 'Diploma', 'UG', 'PG', 'PhD', 'Other'];
const ADDR_FIELDS = [
    { k: 'line1', label: 'Address Line 1', full: true }, { k: 'line2', label: 'Address Line 2', full: true },
    { k: 'city', label: 'City' }, { k: 'state', label: 'State' },
    { k: 'country', label: 'Country' }, { k: 'postalCode', label: 'Postal Code' },
];
const EDU_FIELDS = [
    { k: 'qualification', label: 'Qualification', select: EDU_QUALIFICATIONS },
    { k: 'courseOrSpecialization', label: 'Course / Specialization' },
    { k: 'institution', label: 'Institution' },
    { k: 'boardOrUniversity', label: 'Board / University' },
    { k: 'yearOfPassing', label: 'Year of Passing', num: true },
    { k: 'percentageOrCgpa', label: 'Percentage / CGPA' },
];
const PREV_FIELDS = [
    { k: 'companyName', label: 'Company Name' }, { k: 'designation', label: 'Designation' },
    { k: 'experienceYears', label: 'Experience (years)', num: true },
    { k: 'startDate', label: 'Start Date', date: true }, { k: 'endDate', label: 'End Date', date: true },
    { k: 'lastDrawnSalary', label: 'Last Drawn Salary', num: true },
    { k: 'reasonForLeaving', label: 'Reason for Leaving', full: true },
];
const emptyAddress = () => ({ line1: '', line2: '', city: '', state: '', country: '', postalCode: '' });
const emptyEdu = () => ({ qualification: '', courseOrSpecialization: '', institution: '', boardOrUniversity: '', yearOfPassing: '', percentageOrCgpa: '' });
const emptyPrev = () => ({ companyName: '', designation: '', experienceYears: '', startDate: '', endDate: '', lastDrawnSalary: '', reasonForLeaving: '' });

const readAddress = (a) => ({ ...emptyAddress(), ...(a && typeof a === 'object' ? a : {}) });
const readEdu = (e = {}) => ({ ...emptyEdu(), ...e, yearOfPassing: e.yearOfPassing ?? '', percentageOrCgpa: e.percentageOrCgpa ?? '' });
const readPrev = (p = {}) => ({
    ...emptyPrev(), ...p,
    experienceYears: p.experienceYears ?? '', lastDrawnSalary: p.lastDrawnSalary ?? '',
    startDate: toInputDate(p.startDate), endDate: toInputDate(p.endDate),
});
const addrOut = (a = {}) => ({ line1: txt(a.line1), line2: txt(a.line2), city: txt(a.city), state: txt(a.state), country: txt(a.country), postalCode: txt(a.postalCode) });

// The edit form for a loaded employee: every editable field, both addresses, education and employment history.
export const toEditForm = (emp) => {
    const init = {};
    EDITABLE_FIELDS.forEach((f) => {
        const v = emp[f.k];
        init[f.k] = f.type === 'date' ? toInputDate(v) : (v ?? '');
    });
    init.presentAddress = readAddress(emp.presentAddress);
    init.permanentAddress = readAddress(emp.permanentAddress);
    init.sameAsPresent = false;
    init.certifications = emp.certifications ?? '';
    init.partTimeCalc = emp.salaryCalculationMethod || PART_TIME_CALC.key;
    init.partTimeDaysPerMonth = emp.daysWorkingInMonth ?? '';
    init.educations = Array.isArray(emp.educations) && emp.educations.length ? emp.educations.map(readEdu) : [emptyEdu()];
    init.previousEmployments = Array.isArray(emp.previousEmployments) && emp.previousEmployments.length ? emp.previousEmployments.map(readPrev) : [emptyPrev()];
    return init;
};

export const buildUpdatePayload = (id, form) => {
    const out = { id };
    EDITABLE_FIELDS.forEach((f) => {
        if (f.type === 'date') out[f.k] = toApiDate(form[f.k]);
        else if (f.num) out[f.k] = numOrNull(form[f.k]);
        else out[f.k] = txt(form[f.k]);
    });

    out.presentAddress = addrOut(form.presentAddress);
    out.permanentAddress = form.sameAsPresent ? addrOut(form.presentAddress) : addrOut(form.permanentAddress);

    out.certifications = txt(form.certifications);

    // UpdateEmployee overwrites these on every save (omitted = cleared), so they are always sent.
    const gross = form.employmentType === PART_TIME_TYPE && form.partTimeCalc === 'GrossPartTime';
    out.salaryCalculationMethod = gross ? 'GrossPartTime' : 'GrossCalendarDays';
    out.daysWorkingInMonth = gross ? numOrNull(form.partTimeDaysPerMonth) : null;

    out.educations = (form.educations || [])
        .map((e) => ({
            qualification: txt(e.qualification), courseOrSpecialization: txt(e.courseOrSpecialization),
            institution: txt(e.institution), boardOrUniversity: txt(e.boardOrUniversity),
            yearOfPassing: numOrNull(e.yearOfPassing), percentageOrCgpa: txt(e.percentageOrCgpa),
        }))
        .filter((e) => e.qualification || e.courseOrSpecialization || e.institution || e.boardOrUniversity || e.yearOfPassing != null || e.percentageOrCgpa);

    out.previousEmployments = (form.previousEmployments || [])
        .map((p) => ({
            companyName: txt(p.companyName), designation: txt(p.designation),
            experienceYears: numOrNull(p.experienceYears),
            startDate: toApiDate(p.startDate), endDate: toApiDate(p.endDate),
            lastDrawnSalary: numOrNull(p.lastDrawnSalary), reasonForLeaving: txt(p.reasonForLeaving),
        }))
        .filter((p) => p.companyName || p.designation || p.experienceYears != null || p.startDate || p.endDate || p.lastDrawnSalary != null || p.reasonForLeaving);

    return out;
};

// The two ways a part-timer's daily salary can be worked out (same choice as on the onboarding form).
function PartTimeCalcCards({ value, onChange }) {
    return (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            {PART_TIME_CALCS.map((c) => {
                const on = value === c.key;
                return (
                    <Box key={c.key} onClick={() => onChange(c.key)}
                        sx={{ flex: 1, px: 1.8, py: 1.3, borderRadius: '7px', cursor: 'pointer', transition: '0.2s',
                            border: `2px solid ${on ? '#DC2626' : '#E5E7EB'}`, bgcolor: on ? '#FEF2F2' : '#FAFAFA',
                            '&:hover': { borderColor: on ? '#DC2626' : '#F87171' } }}>
                        <Stack direction="row" spacing={1}>
                            <Box sx={{ width: 16, height: 16, mt: '2px', borderRadius: '50%', border: `2px solid ${on ? '#DC2626' : '#D1D5DB'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {on && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#DC2626' }} />}
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: on ? '#991B1B' : '#333' }}>{c.label}</Typography>
                                <Typography sx={{ fontSize: 11.5, color: on ? '#B91C1C' : '#777', fontStyle: 'italic' }}>{c.formula}</Typography>
                                <Typography sx={{ fontSize: 10.5, color: on ? '#DC2626' : '#9CA3AF', fontWeight: 600, mt: 0.4 }}>{c.example}</Typography>
                            </Box>
                        </Stack>
                    </Box>
                );
            })}
        </Stack>
    );
}

// Eight of these stack down the page, so the header is a quiet white bar with a
// tinted icon rather than a purple gradient band — repeated, the band striped
// the whole page and made every section shout equally.
function SectionCard({ icon: Icon, title, action, children }) {
    return (
        <Box sx={{
            ...card, p: 0, overflow: 'hidden', borderRadius: '11px',
            transition: 'box-shadow .2s ease, border-color .2s ease',
            '&:hover': { boxShadow: '0 12px 28px -18px rgba(16,24,40,0.24)', borderColor: '#E4E0F6' },
        }}>
            <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', px: 2.4, py: 1.7, borderBottom: '1px solid #F1F0F9' }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon sx={{ fontSize: 17, color: PRIMARY }} />
                </Box>
                <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: '#0F172A', flex: 1, letterSpacing: '-0.2px' }}>{title}</Typography>
                {action}
            </Stack>
            <Box sx={{ p: 2.5 }}>{children}</Box>
        </Box>
    );
}

const Label = ({ children }) => <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5 }}>{children}</Typography>;
const NotProvided = () => <Typography sx={{ fontSize: 13, color: '#C4C9D4', fontStyle: 'italic' }}>Not provided</Typography>;

const subHead = { fontSize: 12.5, fontWeight: 800, color: '#475569', mb: 1 };
const itemBox = { border: '1px solid #EEF0F6', borderRadius: '9px', p: 1.6, bgcolor: '#FBFAFF' };
const chipSx = { height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: PRIMARY };
const checkboxBox = (on) => ({ width: 18, height: 18, borderRadius: '5px', border: `1.5px solid ${on ? PRIMARY : '#CBD5E1'}`, bgcolor: on ? PRIMARY : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' });

const EditInput = ({ f, value, onChange, error, helperText }) => (
    f.select ? (
        <TextField select fullWidth size="small" value={value || ''} onChange={onChange} sx={field}>
            <MenuItem value=""><em>—</em></MenuItem>
            {f.select.map((o) => <MenuItem key={o} value={o} sx={{ fontSize: 13.5 }}>{o}</MenuItem>)}
        </TextField>
    ) : (
        <TextField
            fullWidth size="small"
            type={f.date ? 'date' : (f.num ? 'number' : 'text')}
            value={value ?? ''} onChange={onChange} sx={field}
            error={error} helperText={helperText}
            slotProps={{
                ...(f.date ? { inputLabel: { shrink: true } } : {}),
                ...((f.min || f.max) ? { htmlInput: { ...(f.min ? { min: f.min } : {}), ...(f.max ? { max: f.max } : {}) } } : {}),
            }}
        />
    )
);
const ViewVal = ({ f, v }) => {
    const has = v !== null && v !== undefined && String(v) !== '';
    if (!has) return <NotProvided />;
    return <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0F172A' }}>{f?.date ? fmtApiDate(v) : String(v)}</Typography>;
};

export default function EmployeeDetailPage() {
    const navigate = useNavigate();
    const { id } = useParams();

    const { departmentNames, designationNames } = useOrgOptions();

    const [emp, setEmp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const [mode, setMode] = useState('view');
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const [pwdOpen, setPwdOpen] = useState(false);
    const [pwd, setPwd] = useState({ password: '', confirmPassword: '' });
    const [showPwd, setShowPwd] = useState(false);
    const [pwdSaving, setPwdSaving] = useState(false);

    const [snack, setSnack] = useState({ msg: '', severity: 'success' });
    const notify = (msg, severity = 'success') => setSnack({ msg, severity });

    const userTypeId = useSelector(selectUserTypeId);
    const canRemove = userTypeId === USER_TYPE.MASTER_ADMIN || userTypeId === USER_TYPE.ADMIN;

    // The hero is sticky, so both the rail's offset and each section's scroll
    // target depend on how tall it currently is — and it grows in edit mode.
    const heroRef = useRef(null);
    const [heroH, setHeroH] = useState(120);
    useEffect(() => {
        const el = heroRef.current;
        if (!el || typeof ResizeObserver !== 'function') return undefined;
        const ro = new ResizeObserver(() => setHeroH(el.offsetHeight));
        ro.observe(el);
        setHeroH(el.offsetHeight);
        return () => ro.disconnect();
        // Re-attach when the hero actually mounts — it is behind the loading and
        // error early returns. Without a dep list this rebuilt the observer on
        // every render and fed setHeroH straight back into it.
    }, [loading, loadError]);

    // scrollMarginTop keeps a jumped-to section clear of the sticky hero.
    const Anchor = useCallback(
        ({ id, children }) => (
            <Box id={`sec-${id}`} sx={{ scrollMarginTop: `${heroH + 20}px` }}>{children}</Box>
        ),
        [heroH],
    );

    const [activeSection, setActiveSection] = useState(PAGE_SECTIONS[0].id);
    const jumpTo = useCallback((id) => {
        setActiveSection(id);
        document.getElementById(`sec-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    // A thin band just under the hero decides what counts as "current" —
    // whichever section is crossing it wins.
    useEffect(() => {
        const nodes = PAGE_SECTIONS
            .map((s) => document.getElementById(`sec-${s.id}`))
            .filter(Boolean);
        if (!nodes.length || typeof IntersectionObserver !== 'function') return undefined;

        const io = new IntersectionObserver(
            (entries) => {
                const hit = entries.filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
                if (hit) setActiveSection(hit.target.id.replace(/^sec-/, ''));
            },
            { rootMargin: `-${heroH + 24}px 0px -68% 0px`, threshold: 0 },
        );
        nodes.forEach((n) => io.observe(n));
        return () => io.disconnect();
    }, [heroH, loading]);
    const [removeOpen, setRemoveOpen] = useState(false);
    const [settleOpen, setSettleOpen] = useState(false);
    const [removeText, setRemoveText] = useState('');
    const [removing, setRemoving] = useState(false);
    const removeReady = removeText === REMOVE_PHRASE;

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const { data: body } = await http.get(GetEmployeeById, { params: { id } });
            if (body?.error) throw new Error(body.message || 'Could not load this employee.');
            setEmp(body?.data || null);
            setMode('view');
        } catch (err) {
            setLoadError(err?.response || err?.request ? apiErrorMessage(err, 'Could not load this employee.') : err.message);
            setEmp(null);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const fullName = joinName(emp?.firstName, emp?.lastName);
    const active = String(emp?.status || '').toLowerCase() === 'active';

    const { setCrumb } = useOutletContext() || {};
    useEffect(() => { if (setCrumb && fullName) setCrumb(fullName); }, [setCrumb, fullName]);

    const startEdit = () => {
        setForm(toEditForm(emp));
        setMode('edit');
    };
    const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const today = todayInput();

    const setAddr = (which, key) => (e) => setForm((f) => ({ ...f, [which]: { ...f[which], [key]: e.target.value } }));
    const toggleSameAsPresent = () => setForm((f) => ({ ...f, sameAsPresent: !f.sameAsPresent }));
    const setEdu = (i, key) => (e) => setForm((f) => ({ ...f, educations: f.educations.map((row, idx) => (idx === i ? { ...row, [key]: e.target.value } : row)) }));
    const addEdu = () => setForm((f) => (f.educations.length >= MAX_EDU ? f : { ...f, educations: [...f.educations, emptyEdu()] }));
    const removeEdu = (i) => setForm((f) => ({ ...f, educations: f.educations.length <= 1 ? f.educations : f.educations.filter((_, idx) => idx !== i) }));
    const setPrev = (i, key) => (e) => setForm((f) => ({ ...f, previousEmployments: f.previousEmployments.map((row, idx) => (idx === i ? { ...row, [key]: e.target.value } : row)) }));
    const addPrev = () => setForm((f) => (f.previousEmployments.length >= MAX_PREV ? f : { ...f, previousEmployments: [...f.previousEmployments, emptyPrev()] }));
    const removePrev = (i) => setForm((f) => ({ ...f, previousEmployments: f.previousEmployments.length <= 1 ? f.previousEmployments : f.previousEmployments.filter((_, idx) => idx !== i) }));

    const gridSize = (f) => ({ xs: 12, sm: f.full ? 12 : 6, md: f.full ? 12 : 4 });

    const renderGeneric = (sec) => (
        <SectionCard key={sec.id} icon={sec.icon} title={sec.title}>
            <Grid container spacing={mode === 'edit' ? 2 : 2.5}>
                {sec.fields.map((f) => {
                    const editing = mode === 'edit';
                    const raw = editing ? form[f.k] : emp[f.k];
                    let options = f.options;
                    if (f.k === 'department') options = departmentNames;
                    if (f.k === 'designation') options = designationNames;
                    if ((f.k === 'department' || f.k === 'designation') && raw && !options.includes(raw)) options = [...options, raw];
                    const isDob = f.k === 'dateOfBirth';
                    const dobFuture = isDob && editing && Boolean(raw) && String(raw) > today;
                    const spec = { ...f, date: f.type === 'date', max: isDob ? today : undefined, select: f.type === 'select' ? options : undefined };
                    return (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.k}>
                            <Label>{f.label}</Label>
                            {editing
                                ? <EditInput f={spec} value={raw} onChange={setF(f.k)} error={dobFuture} helperText={dobFuture ? 'Date of birth can’t be in the future' : undefined} />
                                : <ViewVal f={{ date: f.type === 'date' }} v={raw} />}
                        </Grid>
                    );
                })}
                {sec.id === 'employment' && (mode === 'edit' ? form.employmentType : emp.employmentType) === PART_TIME_TYPE && (() => {
                    const editing = mode === 'edit';
                    const calc = calcByKey(editing ? form.partTimeCalc : emp.salaryCalculationMethod);
                    const days = editing ? form.partTimeDaysPerMonth : emp.daysWorkingInMonth;
                    return (
                        <>
                            {calc.key === 'GrossPartTime' && (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Label>Days Working in a Month</Label>
                                    {editing
                                        ? <EditInput f={{ num: true, min: 1, max: 31 }} value={days} onChange={setF('partTimeDaysPerMonth')} error={!(Number(days) >= 1 && Number(days) <= 31)} />
                                        : <ViewVal v={days ? `${days} days` : ''} />}
                                </Grid>
                            )}
                            <Grid size={12}>
                                <Label>Salary Calculation</Label>
                                {editing
                                    ? <PartTimeCalcCards value={form.partTimeCalc} onChange={(k) => setForm((f) => ({ ...f, partTimeCalc: k }))} />
                                    : <ViewVal v={`${calc.label} — ${calc.formula}`} />}
                            </Grid>
                        </>
                    );
                })()}
                {sec.id === 'employment' && (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Label>Experience (this company)</Label>
                        {/* Counted from the date of joining here only; earlier employers are not included. It follows the joining date while editing. */}
                        <ViewVal v={companyExperience(mode === 'edit' ? form?.dateOfJoining : emp.dateOfJoining, emp.status, emp.relieveDate || emp.lastWorkingDay || emp.relievedOn || emp.relievingDate || emp.dateOfLeaving || emp.inactiveOn)} />
                    </Grid>
                )}
            </Grid>
        </SectionCard>
    );

    const renderContact = () => (
        <SectionCard icon={HomeRoundedIcon} title="Contact Information">
            {mode === 'edit' ? (
                <Stack spacing={2.5}>
                    <Box>
                        <Typography sx={subHead}>Present Address</Typography>
                        <Grid container spacing={2}>
                            {ADDR_FIELDS.map((f) => (
                                <Grid size={gridSize(f)} key={f.k}>
                                    <Label>{f.label}</Label>
                                    <EditInput f={f} value={form.presentAddress[f.k]} onChange={setAddr('presentAddress', f.k)} />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                    <Box>
                        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1 }}>
                            <Typography sx={{ ...subHead, mb: 0 }}>Permanent Address</Typography>
                            <Stack direction="row" spacing={0.6} onClick={toggleSameAsPresent} sx={{ alignItems: 'center', cursor: 'pointer' }}>
                                <Box sx={checkboxBox(form.sameAsPresent)}>{form.sameAsPresent && <CheckRoundedIcon sx={{ fontSize: 13, color: '#fff' }} />}</Box>
                                <Typography sx={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>Same as present</Typography>
                            </Stack>
                        </Stack>
                        {form.sameAsPresent ? (
                            <Typography sx={{ fontSize: 12.5, color: '#98A0AE', fontStyle: 'italic' }}>Using the present address.</Typography>
                        ) : (
                            <Grid container spacing={2}>
                                {ADDR_FIELDS.map((f) => (
                                    <Grid size={gridSize(f)} key={f.k}>
                                        <Label>{f.label}</Label>
                                        <EditInput f={f} value={form.permanentAddress[f.k]} onChange={setAddr('permanentAddress', f.k)} />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
                </Stack>
            ) : (
                <Stack spacing={2.5}>
                    {[['Present Address', emp.presentAddress], ['Permanent Address', emp.permanentAddress]].map(([title, a]) => (
                        <Box key={title}>
                            <Typography sx={subHead}>{title}</Typography>
                            <Grid container spacing={2.5}>
                                {ADDR_FIELDS.map((f) => (
                                    <Grid size={gridSize(f)} key={f.k}>
                                        <Label>{f.label}</Label>
                                        <ViewVal f={f} v={a?.[f.k]} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ))}
                </Stack>
            )}
        </SectionCard>
    );

    const renderEducation = () => (
        <SectionCard icon={SchoolRoundedIcon} title="Education">
            {mode === 'edit' ? (
                <Stack spacing={1.5}>
                    {form.educations.map((edu, i) => (
                        <Box key={i} sx={itemBox}>
                            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Chip label={`Qualification ${i + 1}`} size="small" sx={chipSx} />
                                <Tooltip arrow title="Remove"><span>
                                    <IconButton size="small" disabled={form.educations.length <= 1} onClick={() => removeEdu(i)} sx={{ color: '#DC2626', '&.Mui-disabled': { color: '#E5E7EB' } }}><DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} /></IconButton>
                                </span></Tooltip>
                            </Stack>
                            <Grid container spacing={2}>
                                {EDU_FIELDS.map((f) => (
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.k}>
                                        <Label>{f.label}</Label>
                                        <EditInput f={f} value={edu[f.k]} onChange={setEdu(i, f.k)} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ))}
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                        <Button onClick={addEdu} disabled={form.educations.length >= MAX_EDU} startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />} sx={{ ...tonalBtn, height: 34 }}>Add qualification</Button>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: form.educations.length >= MAX_EDU ? '#DC2626' : '#98A0AE' }}>{form.educations.length} / {MAX_EDU}</Typography>
                    </Stack>
                    <Box>
                        <Label>Certifications</Label>
                        <TextField fullWidth size="small" value={form.certifications ?? ''} onChange={setF('certifications')} sx={field} placeholder="e.g. AWS Certified, PMP" />
                    </Box>
                </Stack>
            ) : (
                <Stack spacing={2}>
                    {(Array.isArray(emp.educations) && emp.educations.length) ? emp.educations.map((edu, i) => (
                        <Box key={i} sx={itemBox}>
                            <Chip label={`Qualification ${i + 1}`} size="small" sx={{ ...chipSx, mb: 1 }} />
                            <Grid container spacing={2.5}>
                                {EDU_FIELDS.map((f) => (
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.k}>
                                        <Label>{f.label}</Label>
                                        <ViewVal f={f} v={edu[f.k]} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )) : <NotProvided />}
                    <Box><Label>Certifications</Label><ViewVal f={{}} v={emp.certifications} /></Box>
                </Stack>
            )}
        </SectionCard>
    );

    // Salary increment history: read-only everywhere, including while the rest of the page is in edit mode.
    // The server's field names for it aren't pinned down, so the likely ones are tried in turn.
    const incrementRows = (() => {
        const list = [emp?.increments, emp?.salaryIncrements, emp?.incrementHistory].find(Array.isArray) || [];
        const pick = (r, ...keys) => keys.map((k) => r?.[k]).find((v) => v !== undefined && v !== null && v !== '');
        return list.map((r) => {
            const prev = Number(pick(r, 'previousSalary', 'oldSalary', 'previousCtc', 'fromSalary'));
            const next = Number(pick(r, 'newSalary', 'revisedSalary', 'newCtc', 'toSalary'));
            const pct = pick(r, 'percentage', 'incrementPercentage', 'hikePercent')
                ?? (prev > 0 && next > 0 ? ((next - prev) / prev) * 100 : null);
            return {
                date: pick(r, 'effectiveDate', 'incrementDate', 'date'),
                prev: Number.isFinite(prev) ? prev : null,
                next: Number.isFinite(next) ? next : null,
                pct: pct == null ? null : Number(pct),
                reason: pick(r, 'reason', 'remarks', 'notes') || '',
            };
        }).sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    })();
    const renderIncrements = () => (
        <SectionCard icon={TrendingUpRoundedIcon} title="Increments">
            {incrementRows.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: '#98A0AE', fontStyle: 'italic' }}>No increments recorded yet.</Typography>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 560 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 0.8fr 1.6fr', gap: 2, pb: 1, borderBottom: '1px solid #EEF0F6' }}>
                            {['Effective date', 'Previous', 'New', 'Increase', 'Reason'].map((h) => (
                                <Typography key={h} sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</Typography>
                            ))}
                        </Box>
                        {incrementRows.map((r, i) => (
                            <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 0.8fr 1.6fr', gap: 2, py: 1.2, alignItems: 'center', borderBottom: i < incrementRows.length - 1 ? '1px solid #F4F5F9' : 'none' }}>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{r.date ? fmtApiDate(r.date) : '—'}</Typography>
                                <Typography sx={{ fontSize: 13.5 }}>{r.prev != null ? inr(r.prev) : '—'}</Typography>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{r.next != null ? inr(r.next) : '—'}</Typography>
                                <Box>{r.pct != null && Number.isFinite(r.pct)
                                    ? <Chip label={`${r.pct >= 0 ? '+' : ''}${r.pct.toFixed(1)}%`} size="small" sx={{ ...chipSx, bgcolor: '#E8F7EF', color: '#2F8F5F' }} />
                                    : '—'}</Box>
                                <Typography sx={{ fontSize: 13, color: '#64748B' }}>{r.reason || '—'}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </SectionCard>
    );

    const renderPrevious = () => (
        <SectionCard icon={HistoryRoundedIcon} title="Previous Employment">
            {mode === 'edit' ? (
                <Stack spacing={1.5}>
                    {form.previousEmployments.map((job, i) => (
                        <Box key={i} sx={itemBox}>
                            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Chip label={`Employer ${i + 1}`} size="small" sx={chipSx} />
                                <Tooltip arrow title="Remove"><span>
                                    <IconButton size="small" disabled={form.previousEmployments.length <= 1} onClick={() => removePrev(i)} sx={{ color: '#DC2626', '&.Mui-disabled': { color: '#E5E7EB' } }}><DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} /></IconButton>
                                </span></Tooltip>
                            </Stack>
                            <Grid container spacing={2}>
                                {PREV_FIELDS.map((f) => (
                                    <Grid size={gridSize(f)} key={f.k}>
                                        <Label>{f.label}</Label>
                                        <EditInput f={f} value={job[f.k]} onChange={setPrev(i, f.k)} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ))}
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                        <Button onClick={addPrev} disabled={form.previousEmployments.length >= MAX_PREV} startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />} sx={{ ...tonalBtn, height: 34 }}>Add employer</Button>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: form.previousEmployments.length >= MAX_PREV ? '#DC2626' : '#98A0AE' }}>{form.previousEmployments.length} / {MAX_PREV}</Typography>
                    </Stack>
                </Stack>
            ) : (
                (Array.isArray(emp.previousEmployments) && emp.previousEmployments.length) ? (
                    <Stack spacing={2}>
                        {emp.previousEmployments.map((job, i) => (
                            <Box key={i} sx={itemBox}>
                                <Chip label={`Employer ${i + 1}`} size="small" sx={{ ...chipSx, mb: 1 }} />
                                <Grid container spacing={2.5}>
                                    {PREV_FIELDS.map((f) => (
                                        <Grid size={gridSize(f)} key={f.k}>
                                            <Label>{f.label}</Label>
                                            <ViewVal f={f} v={job[f.k]} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        ))}
                    </Stack>
                ) : <NotProvided />
            )}
        </SectionCard>
    );

    const secById = (id) => SECTIONS.find((s) => s.id === id);

    const openRemove = () => { setRemoveText(''); setRemoveOpen(true); };
    const handleRemove = async () => {
        if (!removeReady || removing) return;
        setRemoving(true);
        try {
            const { data: body } = await http.put(InactivateEmployee, { employeeId: Number(emp?.id ?? id) });
            if (body?.error) throw new Error(body.message || 'Could not remove the employee.');
            notify(`${fullName || 'Employee'} removed.`);
            setRemoveOpen(false);
            navigate('/dashboard/employees');
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not remove the employee.'), 'error');
            setRemoving(false);
        }
    };

    const saveEdit = async () => {
        if (!txt(form.firstName) || !txt(form.personalEmail)) {
            notify('First name and personal email are required.', 'warning');
            return;
        }
        if (form.dateOfBirth && String(form.dateOfBirth) > today) {
            notify('Date of birth can’t be in the future.', 'warning');
            return;
        }
        if (form.employmentType === PART_TIME_TYPE && form.partTimeCalc === 'GrossPartTime' && !(Number(form.partTimeDaysPerMonth) >= 1 && Number(form.partTimeDaysPerMonth) <= 31)) {
            notify('Enter how many days a month this part-time employee comes (1 to 31).', 'warning');
            return;
        }
        setSaving(true);
        try {
            const { data: body } = await http.put(UpdateEmployee, buildUpdatePayload(emp.id, form));
            if (body?.error) throw new Error(body.message || 'Could not save changes.');
            await load();
            notify('Employee details updated');
        } catch (err) {
            notify(err?.response || err?.request ? apiErrorMessage(err, 'Could not save changes.') : err.message, 'error');
        } finally {
            setSaving(false);
        }
    };

    const savePassword = async () => {
        if (!pwd.password.trim()) { notify('Enter a password.', 'warning'); return; }
        if (pwd.password.length < PASSWORD_MIN) { notify(`Password must be at least ${PASSWORD_MIN} characters.`, 'warning'); return; }
        if (pwd.password !== pwd.confirmPassword) { notify('Passwords do not match.', 'warning'); return; }
        setPwdSaving(true);
        try {
            const { data: body } = await http.post(SetEmployeeLoginPassword, {
                employeeId: emp.id,
                password: pwd.password,
                confirmPassword: pwd.confirmPassword,
            });
            if (body?.error) throw new Error(body.message || 'Could not set the password.');
            setPwdOpen(false);
            setPwd({ password: '', confirmPassword: '' });
            notify('Password updated');
        } catch (err) {
            notify(err?.response || err?.request ? apiErrorMessage(err, 'Could not set the password.') : err.message, 'error');
        } finally {
            setPwdSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                <Box sx={{ ...card, p: 2, mb: 1.5 }}>
                    <Stack direction="row" spacing={1.6} sx={{ alignItems: 'center' }}>
                        <Skeleton variant="rounded" width={40} height={40} />
                        <Skeleton variant="circular" width={52} height={52} />
                        <Box><Skeleton variant="text" width={200} height={28} /><Skeleton variant="text" width={260} height={18} /></Box>
                    </Stack>
                </Box>
                <Stack spacing={1.5}>
                    {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={190} sx={{ borderRadius: '7px' }} />)}
                </Stack>
            </Box>
        );
    }

    if (loadError || !emp) {
        return (
            <Box sx={{ p: 2 }}>
                <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', ...card, p: 2.2, bgcolor: '#FEF2F2', border: '1px solid #FECACA' }}>
                    <ErrorOutlineRoundedIcon sx={{ fontSize: 22, color: '#B91C1C' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: '#991B1B' }}>Couldn't load this employee</Typography>
                        <Typography sx={{ fontSize: 12.5, color: '#B91C1C' }}>{loadError || 'Employee not found.'}</Typography>
                    </Box>
                    <Button onClick={load} startIcon={<RefreshRoundedIcon sx={{ fontSize: 17 }} />} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, color: '#B91C1C', bgcolor: '#fff', border: '1px solid #FECACA', borderRadius: '7px', height: 38, px: 2 }}>Retry</Button>
                </Stack>
                <Button onClick={() => navigate('/dashboard/employees')} startIcon={<ArrowBackRoundedIcon />} sx={{ ...tonalBtn, mt: 2, height: 40, px: 2 }}>Back to Employees</Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box ref={heroRef} sx={{
                position: 'sticky', top: 0, zIndex: 6, ...card, p: 2, mb: 1.5,
                background: mode === 'edit' ? `linear-gradient(120deg, #FFFFFF 55%, ${PRIMARY}0E 100%)` : 'linear-gradient(120deg, #FFFFFF 60%, #F7F6FF 100%)',
                borderColor: mode === 'edit' ? PRIMARY_BORDER : '#E6EAF1',
                boxShadow: mode === 'edit' ? `0 8px 24px -14px ${PRIMARY}66` : '0 1px 3px rgba(16,24,40,0.06)',
                transition: 'box-shadow .2s, border-color .2s, background .2s',
            }}>
                <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                    <Stack direction="row" spacing={1.8} sx={{ alignItems: 'center', minWidth: 0 }}>
                        <IconButton onClick={() => navigate('/dashboard/employees')} sx={{ bgcolor: '#F8FAFC', border: '1px solid #E6EAF1', borderRadius: '9px', flexShrink: 0, '&:hover': { bgcolor: '#F1F5F9' } }}>
                            <ArrowBackRoundedIcon sx={{ fontSize: 20, color: '#334155' }} />
                        </IconButton>

                        <Avatar
                            src={emp.profilePhotoUrl || undefined}
                            sx={{
                                width: 68, height: 68, flexShrink: 0, fontSize: 23, fontWeight: 800,
                                bgcolor: PRIMARY_LIGHT, color: PRIMARY,
                                border: `2px solid ${PRIMARY_BORDER}`,
                                filter: active ? 'none' : 'grayscale(0.5)',
                            }}
                        >
                            {initials(fullName)}
                        </Avatar>

                        <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                <Typography sx={{ fontSize: 23, fontWeight: 800, color: '#111827', letterSpacing: '-0.5px', lineHeight: 1.15 }} noWrap>{fullName || '—'}</Typography>
                                {/* A dot rather than a filled chip — one status marker
                                    shouldn't be the loudest thing on the page. */}
                                <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center', height: 22, px: 1, borderRadius: '999px', bgcolor: '#F5F7FA', border: '1px solid #E6EAF1' }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: active ? '#16A34A' : '#94A3B8' }} />
                                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#475569', textTransform: 'capitalize' }}>
                                        {active ? 'Active' : (emp.status || 'Inactive')}
                                    </Typography>
                                </Stack>
                                {mode === 'edit' && (
                                    <Chip icon={<EditRoundedIcon sx={{ fontSize: 13 }} />} label="Editing" size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 800, bgcolor: PRIMARY_LIGHT, color: PRIMARY, border: `1px solid ${PRIMARY_BORDER}`, '& .MuiChip-icon': { color: PRIMARY, ml: 0.5 } }} />
                                )}
                            </Stack>

                            <Typography sx={{ fontSize: 13.5, color: '#6B7280', mt: 0.5 }} noWrap>
                                {emp.designation || 'Staff'}{emp.department ? ` · ${emp.department}` : ''}
                            </Typography>

                            <Stack direction="row" spacing={0.9} sx={{ alignItems: 'center', mt: 0.8, flexWrap: 'wrap' }}>
                                <Chip label={emp.employeeCode || '—'} size="small" sx={{ height: 21, fontSize: 11, fontWeight: 800, bgcolor: PRIMARY_LIGHT, color: PRIMARY, border: `1px solid ${PRIMARY_BORDER}` }} />
                                {emp.createdOn && <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>Added {fmtApiDate(emp.createdOn)}</Typography>}
                            </Stack>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        {mode === 'view' ? (
                            <>
                                <Button onClick={startEdit} startIcon={<EditRoundedIcon sx={{ fontSize: 17 }} />} sx={{ ...tonalBtn, height: 40, px: 2 }}>Edit</Button>
                                {!active && emp.employeeCode && (
                                    <Button onClick={() => setSettleOpen(true)} startIcon={<ReceiptLongRoundedIcon sx={{ fontSize: 17 }} />} sx={{ ...ghostBtn, height: 40, px: 2 }}>Final settlement</Button>
                                )}
                                {canRemove && (
                                    <Button onClick={openRemove} startIcon={<PersonRemoveRoundedIcon sx={{ fontSize: 17 }} />} sx={{ ...dangerBtn, height: 40, px: 2 }}>Remove Employee</Button>
                                )}
                            </>
                        ) : (
                            <>
                                <Button disabled={saving} onClick={() => setMode('view')} sx={{ color: '#64748B', textTransform: 'none', fontWeight: 600, borderRadius: '7px', height: 40, px: 2, border: '1px solid #E6EAF1' }}>Cancel</Button>
                                <Button disabled={saving} onClick={saveEdit} startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveRoundedIcon sx={{ fontSize: 18 }} />} sx={{ ...solidBtn, height: 40, px: 2.4 }}>{saving ? 'Saving…' : 'Save Changes'}</Button>
                            </>
                        )}
                    </Stack>
                </Stack>

                {/* The at-a-glance facts as one divided strip across the full width,
                    instead of four pills crowded under the name. */}
                {(() => {
                    const facts = [
                        ['Email', emp.personalEmail],
                        ['Mobile', emp.personalMobile],
                        ['Joined', emp.dateOfJoining ? fmtApiDate(emp.dateOfJoining) : ''],
                        ['Type', emp.employmentType],
                    ].filter(([, v]) => v);
                    if (!facts.length) return null;
                    return (
                        <Box sx={{
                            mt: 2, pt: 1.8, borderTop: '1px solid #EEF0F6',
                            display: 'grid', gap: 0,
                            gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${facts.length}, 1fr)` },
                        }}>
                            {facts.map(([k, v], i) => (
                                <Box key={k} sx={{ minWidth: 0, px: { xs: 0, md: 2 }, py: { xs: 0.8, md: 0 }, borderLeft: { xs: 'none', md: i === 0 ? 'none' : '1px solid #EEF0F6' }, '&:first-of-type': { pl: { md: 0 } } }}>
                                    <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#98A0AE', textTransform: 'uppercase', letterSpacing: 0.5 }}>{k}</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#334155', mt: 0.3 }} noWrap>{v}</Typography>
                                </Box>
                            ))}
                        </Box>
                    );
                })()}
            </Box>

            {/* No alignItems here: the columns must stretch to the row height so
                the rail inside the left one has room to travel while sticking. */}
            <Box sx={{
                display: 'grid', gap: 1.5,
                gridTemplateColumns: { xs: '1fr', lg: '236px minmax(0, 1fr)' },
            }}>
                <SectionRail activeId={activeSection} onJump={jumpTo} top={heroH + 12} />

                <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                    <Anchor id="credentials">
                        <SectionCard icon={LockRoundedIcon} title="Login Credentials">
                            <Grid container spacing={2.5} sx={{ alignItems: 'center' }}>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Label>Employee Code</Label>
                                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#5B21B6' }}>{emp.employeeCode || '—'}</Typography>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Label>Username (sign-in email)</Label>
                                    {emp.personalEmail
                                        ? <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>{emp.personalEmail}</Typography>
                                        : <Typography sx={{ fontSize: 12.5, color: '#DC2626', fontStyle: 'italic' }}>No email — add a Personal Email to enable sign-in</Typography>}
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Label>Password</Label>
                                    <Button onClick={() => { setPwd({ password: '', confirmPassword: '' }); setShowPwd(false); setPwdOpen(true); }}
                                        startIcon={<VpnKeyRoundedIcon sx={{ fontSize: 16 }} />} sx={{ ...tonalBtn, height: 34, px: 1.6, fontSize: 12.5 }}>
                                        Set / Reset Password
                                    </Button>
                                </Grid>
                            </Grid>
                            <Typography sx={{ fontSize: 11.5, color: '#98A0AE', mt: 1.5 }}>
                                They sign in with their <strong>email</strong> as the username. To change it, edit the Personal Email under Personal Information above and save.
                            </Typography>
                        </SectionCard>
                    </Anchor>

                    <Anchor id="personal">{renderGeneric(secById('personal'))}</Anchor>
                    <Anchor id="employment">{renderGeneric(secById('employment'))}</Anchor>
                    <Anchor id="increments">{renderIncrements()}</Anchor>
                    <Anchor id="contact">{renderContact()}</Anchor>
                    <Anchor id="identity">{renderGeneric(secById('identity'))}</Anchor>
                    <Anchor id="education">{renderEducation()}</Anchor>
                    <Anchor id="previous">{renderPrevious()}</Anchor>
                    <Anchor id="emergency">{renderGeneric(secById('emergency'))}</Anchor>
                    <Anchor id="bank">{renderGeneric(secById('bank'))}</Anchor>
                    <Anchor id="documents"><EmployeeDocuments employeeId={emp.id} /></Anchor>

                    {emp.employeeCode && (
                        <Box sx={{ mt: 1.5 }}><EmployeeAttendanceSummary employeeCode={emp.employeeCode} /></Box>
                    )}
                </Stack>
            </Box>

            <Dialog open={pwdOpen} onClose={() => !pwdSaving && setPwdOpen(false)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogContent sx={{ pt: 3, pb: 1.5 }}>
                    <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center', mb: 2 }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: '10px', bgcolor: PRIMARY_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <VpnKeyRoundedIcon sx={{ color: PRIMARY, fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 16.5, fontWeight: 800, color: '#0F172A' }}>Set Password</Typography>
                            <Typography sx={{ fontSize: 12.5, color: '#98A0AE' }}>{fullName} signs in with <strong>{emp.personalEmail || emp.employeeCode}</strong></Typography>
                        </Box>
                    </Stack>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#334155', mb: 0.6 }}>New password</Typography>
                    <TextField fullWidth size="small" autoFocus type={showPwd ? 'text' : 'password'} value={pwd.password}
                        onChange={(e) => setPwd((p) => ({ ...p, password: e.target.value }))} sx={field}
                        error={Boolean(pwd.password) && pwd.password.length < PASSWORD_MIN}
                        helperText={Boolean(pwd.password) && pwd.password.length < PASSWORD_MIN ? `Password must be at least ${PASSWORD_MIN} characters` : ' '}
                        slotProps={{ input: { endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowPwd((v) => !v)} sx={{ p: 0.3 }}>{showPwd ? <VisibilityOffRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} /> : <VisibilityRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} />}</IconButton></InputAdornment> } }} />
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#334155', mb: 0.6 }}>Confirm password</Typography>
                    <TextField fullWidth size="small" type={showPwd ? 'text' : 'password'} value={pwd.confirmPassword}
                        onChange={(e) => setPwd((p) => ({ ...p, confirmPassword: e.target.value }))}
                        error={Boolean(pwd.confirmPassword) && pwd.password !== pwd.confirmPassword}
                        helperText={Boolean(pwd.confirmPassword) && pwd.password !== pwd.confirmPassword ? 'Passwords do not match' : ' '}
                        sx={field}
                        onKeyDown={(e) => { if (e.key === 'Enter') savePassword(); }} />
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
                    <Button fullWidth disabled={pwdSaving} onClick={() => setPwdOpen(false)} sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '7px', height: 40, border: '1px solid #E5E7EB', color: '#374151' }}>Cancel</Button>
                    <Button fullWidth disabled={pwdSaving} onClick={savePassword}
                        startIcon={pwdSaving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <SaveRoundedIcon sx={{ fontSize: 17 }} />}
                        sx={{ ...solidBtn, height: 40 }}>{pwdSaving ? 'Saving…' : 'Save'}</Button>
                </DialogActions>
            </Dialog>

            <SettlementDialog
                open={settleOpen}
                employeeCode={emp?.employeeCode}
                employeeName={fullName}
                onClose={() => setSettleOpen(false)}
            />

            <Dialog open={removeOpen} onClose={() => !removing && setRemoveOpen(false)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '14px' } } }}>
                <Box sx={{ px: 3, pt: 2.8, pb: 1.5, textAlign: 'center' }}>
                    <Box sx={{ width: 54, height: 54, borderRadius: '50%', bgcolor: '#FEE2E2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 1.4 }}>
                        <ErrorOutlineRoundedIcon sx={{ fontSize: 30, color: '#DC2626' }} />
                    </Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>Remove employee</Typography>
                    <Typography sx={{ fontSize: 13, color: '#64748B', mt: 0.6, lineHeight: 1.6 }}>
                        <strong>{fullName || 'This employee'}</strong>{(emp?.employeeCode || emp?.code) ? ` (${emp.employeeCode || emp.code})` : ''} will no longer be able to log in or do anything from now on. Their past records stay in the system for your history and reports. <strong style={{ color: '#B91C1C' }}>This can&apos;t be undone.</strong>
                    </Typography>
                </Box>
                <DialogContent sx={{ pt: 0.5 }}>
                    <Typography sx={{ fontSize: 12.5, color: '#475569', mb: 0.8 }}>
                        Type <Box component="span" sx={{ fontWeight: 800, color: '#DC2626', fontFamily: 'monospace' }}>{REMOVE_PHRASE}</Box> to continue.
                    </Typography>
                    <TextField
                        autoFocus fullWidth size="small" placeholder={REMOVE_PHRASE} value={removeText}
                        onChange={(e) => setRemoveText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && removeReady) handleRemove(); }}
                        sx={field}
                        slotProps={{ input: { autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setRemoveOpen(false)} disabled={removing} sx={{ color: '#64748B', textTransform: 'none', fontWeight: 600, borderRadius: '7px', height: 40, px: 2, border: '1px solid #E6EAF1' }}>Cancel</Button>
                    <Button onClick={handleRemove} disabled={!removeReady || removing}
                        startIcon={removing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PersonRemoveRoundedIcon sx={{ fontSize: 18 }} />}
                        sx={{ height: 40, px: 2.4, fontWeight: 700, textTransform: 'none', borderRadius: '7px', bgcolor: '#DC2626', color: '#fff', boxShadow: 'none', '&:hover': { bgcolor: '#B91C1C' }, '&.Mui-disabled': { bgcolor: '#FCA5A5', color: '#fff' } }}>
                        {removing ? 'Removing…' : 'Remove employee'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={Boolean(snack.msg)} autoHideDuration={3500} onClose={() => setSnack((s) => ({ ...s, msg: '' }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack((s) => ({ ...s, msg: '' }))} severity={snack.severity} variant="filled" sx={{ borderRadius: '7px' }}>{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
